import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { getAppointmentAvailableSlots, type AppointmentAvailableSlot } from '@/lib/api/appointments';
import { fetchScheduleAvailabilityContext } from '@/lib/api/scheduleAvailability';
import type { WeeklyAvailabilityDay } from '@/lib/api/availability';
import type { TimeOff } from '@/lib/api/availability';
import {
  getIanaTimeZone,
  resolveNutritionistSchedulingTimezone,
} from '@/utils/appointments/appointment-timezone';
import {
  filterSlotsForClientCalendarDay,
  isPublicBookingCalendarDaySelectable,
  isSameClientCalendarDay,
  mergeAvailableSlotsByUtc,
  resolveScheduleLookupDatesForClientDay,
} from '@/utils/appointments/public-booking-calendar';
import { formatInTimezone } from '@/utils/timezone-utils';
import { showErrorToast } from '@/utils/toast-utils';

export interface ScheduleBlockedDateRange {
  startDate: string;
  endDate: string;
}

interface UseScheduleAvailabilityOptions {
  enabled: boolean;
  scheduleMonth: Date;
  selectedDate: Date | null;
  durationMinutes: number;
  /** When rescheduling, pass existing appointment id so its slot stays available. */
  excludeAppointmentId?: string;
  /** Original start instant (ms) — use a primitive so slot fetch deps stay stable. */
  originalAppointmentStartTimeMs?: number | null;
  /** Extra date ranges to block before they are saved as time off. */
  blockedDateRanges?: ScheduleBlockedDateRange[];
}

const DATE_ONLY_PATTERN = /^(\d{4}-\d{2}-\d{2})/;

const normalizeDateOnly = (value: string): string => {
  const match = String(value || '').trim().match(DATE_ONLY_PATTERN);
  return match ? match[1] : '';
};

export function useScheduleAvailability({
  enabled,
  scheduleMonth,
  selectedDate,
  durationMinutes,
  excludeAppointmentId,
  originalAppointmentStartTimeMs,
  blockedDateRanges = [],
}: UseScheduleAvailabilityOptions) {
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [scheduleDays, setScheduleDays] = useState<WeeklyAvailabilityDay[]>([]);
  const [timeOffEntries, setTimeOffEntries] = useState<TimeOff[]>([]);
  const [slotsForSelectedDate, setSlotsForSelectedDate] = useState<AppointmentAvailableSlot[]>([]);
  const [scheduleTimezone, setScheduleTimezone] = useState<string | undefined>();

  const displayTimezone = getIanaTimeZone();
  const resolvedScheduleTimezone = scheduleTimezone || displayTimezone;
  const originalAppointmentStartUtc =
    originalAppointmentStartTimeMs != null
      ? new Date(originalAppointmentStartTimeMs).toISOString()
      : undefined;

  const blockedDateRangesKey = blockedDateRanges
    .map((range) => `${normalizeDateOnly(range.startDate)}:${normalizeDateOnly(range.endDate)}`)
    .join('|');
  const normalizedBlockedDateRanges = useMemo(
    () =>
      blockedDateRangesKey
        .split('|')
        .map((range) => {
          const [startDate, endDate] = range.split(':');
          if (!startDate) return null;
          return { startDate, endDate: endDate || startDate };
        })
        .filter((range): range is ScheduleBlockedDateRange => Boolean(range)),
    [blockedDateRangesKey],
  );

  const mergedTimeOff = useMemo(
    () => [
      ...timeOffEntries,
      ...normalizedBlockedDateRanges.map((range, index) => ({
        id: `pending-blocked-date-${index}`,
        startDate: range.startDate,
        endDate: range.endDate,
        reason: 'Blocked Date',
      })),
    ],
    [normalizedBlockedDateRanges, timeOffEntries],
  );

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(scheduleMonth);
    const monthEnd = endOfMonth(scheduleMonth);
    const start = startOfWeek(monthStart, { weekStartsOn: 1 });
    const end = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [scheduleMonth]);

  /** Browser-local yyyy-MM-dd — matches public booking link date param. */
  const selectedDayKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;

  const isDayAvailable = useCallback(
    (day: Date): boolean =>
      isPublicBookingCalendarDaySelectable(day, {
        timeOff: mergedTimeOff,
        clientTimezone: displayTimezone,
        scheduleTimezone: resolvedScheduleTimezone,
        scheduleDays,
        originalAppointmentStartUtc,
      }),
    [displayTimezone, mergedTimeOff, originalAppointmentStartUtc, resolvedScheduleTimezone, scheduleDays],
  );

  useEffect(() => {
    if (!enabled) {
      setScheduleDays([]);
      setTimeOffEntries([]);
      setScheduleTimezone(undefined);
      setCalendarLoading(false);
      setCalendarLoaded(false);
      return;
    }

    let cancelled = false;

    const loadCalendarAvailability = async () => {
      setCalendarLoading(true);
      setCalendarLoaded(false);
      try {
        const monthStart = startOfMonth(scheduleMonth);
        const monthEnd = endOfMonth(scheduleMonth);
        const rangeStart = startOfWeek(monthStart, { weekStartsOn: 1 });
        const rangeEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

        const context = await fetchScheduleAvailabilityContext(rangeStart, rangeEnd, {
          includeAppointments: false,
        });
        if (cancelled) return;

        setScheduleDays(context.schedule.days ?? []);
        setTimeOffEntries(context.timeOff);
        setScheduleTimezone(resolveNutritionistSchedulingTimezone(context.schedule.timezone));
      } catch (error) {
        if (!cancelled) {
          setScheduleDays([]);
          setTimeOffEntries([]);
          setScheduleTimezone(undefined);
          showErrorToast(error, 'Failed to load availability');
        }
      } finally {
        if (!cancelled) {
          setCalendarLoading(false);
          setCalendarLoaded(true);
        }
      }
    };

    void loadCalendarAvailability();

    return () => {
      cancelled = true;
    };
  }, [enabled, normalizedBlockedDateRanges, scheduleMonth]);

  useEffect(() => {
    if (!enabled || !selectedDayKey || !selectedDate) {
      setSlotsForSelectedDate([]);
      setSlotsLoading(false);
      return;
    }

    if (!calendarLoaded) {
      setSlotsForSelectedDate([]);
      setSlotsLoading(true);
      return;
    }

    if (!isDayAvailable(selectedDate)) {
      setSlotsForSelectedDate([]);
      setSlotsLoading(false);
      return;
    }

    let cancelled = false;

    const loadSlots = async () => {
      setSlotsLoading(true);
      try {
        const lookupDates = resolveScheduleLookupDatesForClientDay(
          selectedDate,
          resolvedScheduleTimezone,
          displayTimezone,
          scheduleDays,
          originalAppointmentStartUtc ? { originalAppointmentStartUtc } : undefined,
        );

        const responses = await Promise.all(
          lookupDates.map((date) =>
            getAppointmentAvailableSlots({
              date,
              duration: durationMinutes,
              timezone: displayTimezone,
              appointmentId: excludeAppointmentId,
            }),
          ),
        );

        if (!cancelled) {
          const isOriginalAppointmentClientDay =
            originalAppointmentStartTimeMs != null &&
            isSameClientCalendarDay(
              selectedDate,
              new Date(originalAppointmentStartTimeMs),
              displayTimezone,
            );

          let updatedSlots = mergeAvailableSlotsByUtc(
            responses.flatMap((response) => response.availableSlots),
          );

          if (!isOriginalAppointmentClientDay) {
            updatedSlots = filterSlotsForClientCalendarDay(
              updatedSlots,
              selectedDate,
              displayTimezone,
            );
          }

          if (originalAppointmentStartTimeMs != null && isOriginalAppointmentClientDay) {
            const originalDate = new Date(originalAppointmentStartTimeMs);
            const originalTimeStr = formatInTimezone(originalDate, displayTimezone, 'HH:mm');
            const hasOriginalSlot = updatedSlots.some((slot) => slot.startTime === originalTimeStr);
            if (!hasOriginalSlot) {
              const originalEndMs = originalAppointmentStartTimeMs + durationMinutes * 60_000;
              const endTimeStr = formatInTimezone(new Date(originalEndMs), displayTimezone, 'HH:mm');
              updatedSlots = [
                ...updatedSlots,
                {
                  startTime: originalTimeStr,
                  endTime: endTimeStr,
                  startTimeUtc: new Date(originalAppointmentStartTimeMs).toISOString(),
                  endTimeUtc: new Date(originalEndMs).toISOString(),
                },
              ];
              updatedSlots.sort((a, b) => a.startTime.localeCompare(b.startTime));
            }
          }

          setSlotsForSelectedDate(updatedSlots);
          const responseScheduleTimezone = responses.find((response) => response.scheduleTimezone)
            ?.scheduleTimezone;
          if (responseScheduleTimezone) {
            setScheduleTimezone(responseScheduleTimezone);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setSlotsForSelectedDate([]);
          showErrorToast(error, 'Failed to load available times');
        }
      } finally {
        if (!cancelled) {
          setSlotsLoading(false);
        }
      }
    };

    void loadSlots();

    return () => {
      cancelled = true;
    };
  }, [
    calendarLoaded,
    displayTimezone,
    durationMinutes,
    enabled,
    excludeAppointmentId,
    isDayAvailable,
    originalAppointmentStartTimeMs,
    originalAppointmentStartUtc,
    resolvedScheduleTimezone,
    scheduleDays,
    selectedDate,
    selectedDayKey,
  ]);

  return {
    loading: calendarLoading || slotsLoading,
    calendarLoaded,
    calendarLoading,
    slotsLoading,
    calendarDays,
    slotsForSelectedDate,
    scheduleTimezone: scheduleTimezone || displayTimezone,
    /** @deprecated Use `scheduleTimezone` */
    slotsTimezone: scheduleTimezone || displayTimezone,
    isDayAvailable,
  };
}
